import React from 'react';
import {
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    createEmployee,
    deleteEmployee,
    getCostCenters,
    getEmployees,
    updateEmployee,
} from '../../api';
import AppDatePicker from '../shared/AppDatePicker';

const emptyEmployee = {
    employeeNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    iban: '',
    taxId: '',
    socialSecurityNumber: '',
    dateOfBirth: '',
    employmentStart: '',
    employmentEnd: '',
    costCenterId: '',
};

const PayrollIntegration = () => {
    const [employees, setEmployees] = React.useState([]);
    const [costCenters, setCostCenters] = React.useState([]);
    const [form, setForm] = React.useState(emptyEmployee);
    const [editingId, setEditingId] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const loadEmployees = React.useCallback(async () => {
        try {
            const res = await getEmployees();
            setEmployees(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Failed to load employees', error);
        }
    }, []);

    const loadCostCenters = React.useCallback(async () => {
        try {
            const res = await getCostCenters();
            setCostCenters(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Failed to load cost centers', error);
        }
    }, []);

    React.useEffect(() => {
        loadEmployees();
        loadCostCenters();
    }, [loadEmployees, loadCostCenters]);

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEdit = (employee) => {
        setEditingId(employee.id);
        setForm({
            employeeNumber: employee.employeeNumber || '',
            firstName: employee.firstName || '',
            lastName: employee.lastName || '',
            email: employee.email || '',
            iban: employee.iban || '',
            taxId: employee.taxId || '',
            socialSecurityNumber: employee.socialSecurityNumber || '',
            dateOfBirth: employee.dateOfBirth || '',
            employmentStart: employee.employmentStart || '',
            employmentEnd: employee.employmentEnd || '',
            costCenterId: employee.costCenterId || '',
        });
    };

    const resetForm = () => {
        setForm(emptyEmployee);
        setEditingId(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form };
            if (editingId) {
                await updateEmployee(editingId, payload);
            } else {
                await createEmployee(payload);
            }
            await loadEmployees();
            resetForm();
        } catch (error) {
            console.error('Failed to save employee', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (employee) => {
        if (!employee?.id) {
            return;
        }
        const confirmed = window.confirm(`Mitarbeiter ${employee.displayName || employee.firstName} wirklich löschen?`);
        if (!confirmed) {
            return;
        }
        setLoading(true);
        try {
            await deleteEmployee(employee.id);
            await loadEmployees();
            if (editingId === employee.id) {
                resetForm();
            }
        } catch (error) {
            console.error('Failed to delete employee', error);
        } finally {
            setLoading(false);
        }
    };

    const costCenterOptions = React.useMemo(() => (
        costCenters.map((cc) => ({
            value: cc.id,
            label: cc.displayName || cc.name || 'Kostenstelle',
        }))
    ), [costCenters]);

    const formatCurrency = (value) => {
        if (value == null) {
            return '';
        }
        try {
            return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value));
        } catch (error) {
            return value;
        }
    };

    const formatDate = (value) => {
        if (!value) {
            return '';
        }
        try {
            return new Intl.DateTimeFormat('de-DE').format(new Date(value));
        } catch (error) {
            return value;
        }
    };

    const lastPayroll = (employee) => {
        if (!employee?.payrollStatements || employee.payrollStatements.length === 0) {
            return null;
        }
        return employee.payrollStatements[0];
    };

    return (
        <Box sx={{ display: 'grid', gap: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Mitarbeiterstamm
                </Typography>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Personalnummer"
                                name="employeeNumber"
                                value={form.employeeNumber}
                                onChange={handleFormChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                required
                                label="Vorname"
                                name="firstName"
                                value={form.firstName}
                                onChange={handleFormChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                required
                                label="Nachname"
                                name="lastName"
                                value={form.lastName}
                                onChange={handleFormChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="E-Mail"
                                name="email"
                                value={form.email}
                                onChange={handleFormChange}
                                fullWidth
                                size="small"
                                type="email"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="IBAN"
                                name="iban"
                                value={form.iban}
                                onChange={handleFormChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Steuer-ID"
                                name="taxId"
                                value={form.taxId}
                                onChange={handleFormChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Sozialversicherungsnummer"
                                name="socialSecurityNumber"
                                value={form.socialSecurityNumber}
                                onChange={handleFormChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AppDatePicker
                                label="Geburtsdatum"
                                name="dateOfBirth"
                                value={form.dateOfBirth || ''}
                                onChange={(date) => setForm((prev) => ({ ...prev, dateOfBirth: date }))}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AppDatePicker
                                label="Eintritt"
                                name="employmentStart"
                                value={form.employmentStart || ''}
                                onChange={(date) => setForm((prev) => ({ ...prev, employmentStart: date }))}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AppDatePicker
                                label="Austritt"
                                name="employmentEnd"
                                value={form.employmentEnd || ''}
                                onChange={(date) => setForm((prev) => ({ ...prev, employmentEnd: date }))}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="cost-center-label">Kostenstelle</InputLabel>
                                <Select
                                    labelId="cost-center-label"
                                    label="Kostenstelle"
                                    name="costCenterId"
                                    value={form.costCenterId || ''}
                                    onChange={handleFormChange}
                                >
                                    <MenuItem value=""><em>- Keine -</em></MenuItem>
                                    {costCenterOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Button type="submit" variant="contained" disabled={loading}>
                            {editingId ? 'Mitarbeiter aktualisieren' : 'Mitarbeiter anlegen'}
                        </Button>
                        {editingId && (
                            <Button variant="text" onClick={resetForm} disabled={loading}>
                                Abbrechen
                            </Button>
                        )}
                    </Stack>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                    Übersicht
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Personalnummer</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>E-Mail</TableCell>
                            <TableCell>Kostenstelle</TableCell>
                            <TableCell>Letzte Abrechnung</TableCell>
                            <TableCell align="right">Netto</TableCell>
                            <TableCell align="right">Brutto</TableCell>
                            <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {employees.map((employee) => {
                            const payroll = lastPayroll(employee);
                            return (
                                <TableRow key={employee.id} hover>
                                    <TableCell>{employee.employeeNumber || '—'}</TableCell>
                                    <TableCell>{employee.displayName || `${employee.firstName || ''} ${employee.lastName || ''}`}</TableCell>
                                    <TableCell>{employee.email || '—'}</TableCell>
                                    <TableCell>{employee.costCenterDisplayName || '—'}</TableCell>
                                    <TableCell>{payroll ? formatDate(payroll.payrollPeriod) : '—'}</TableCell>
                                    <TableCell align="right">{payroll ? formatCurrency(payroll.netSalary) : '—'}</TableCell>
                                    <TableCell align="right">{payroll ? formatCurrency(payroll.grossSalary) : '—'}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Button size="small" onClick={() => handleEdit(employee)} disabled={loading}>
                                                Bearbeiten
                                            </Button>
                                            <Button size="small" color="error" onClick={() => handleDelete(employee)} disabled={loading}>
                                                Löschen
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {employees.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    Keine Mitarbeiter erfasst.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
};

export default PayrollIntegration;
